import React from 'react'
import {nanoid} from 'nanoid'
import {BlockProps} from '../atoms/blockList'
import {ListInput, FieldProps, ListValue} from '../atoms/listInput'
import {Configs, isFunctionalUpdate} from '@dudagroup/editor'
import {
  LinkListBlockValue,
  LinkListBlockValueItem,
  ListTypeBlockValue,
  LinkObject,
  ExternalLinkTarget,
} from './types'
import {Form, Input, SelectPicker} from 'rsuite'
import {getEmptyI18nValue} from '../utility'
import update from 'immutability-helper'
import {LinkComponent} from '../atoms/LinkComponent'
import {useTranslation} from 'react-i18next'
import {I18nWrapper} from '../atoms/i18nWrapper'

// ----- Adapters: storage <-> UI -----

type ListWrapper<T> = {id: string; expanded: boolean; content: T}

function toListValues(
  wrappers: Array<ListWrapper<LinkListBlockValueItem>>
): Array<ListValue<LinkListBlockValueItem>> {
  return wrappers.map((w) => ({id: w.id, value: w.content}))
}

function fromListValues(
  listValues: Array<ListValue<LinkListBlockValueItem>>,
  prev: Array<ListWrapper<LinkListBlockValueItem>>
): Array<ListWrapper<LinkListBlockValueItem>> {
  const expandedById = new Map(prev.map((w) => [w.id, w.expanded]))
  return listValues.map((lv) => ({
    id: lv.id ?? nanoid(),
    expanded: expandedById.get(lv.id) ?? false,
    content: lv.value,
  }))
}

// Fresh default item for “Add”
function makeDefaultItem(configs: Configs): LinkListBlockValueItem {
  const languages = configs.apiConfig.languages
  return {
    linkText: getEmptyI18nValue(languages, ''),
    link: {
      external: {
        href: getEmptyI18nValue(languages, 'https://'),
        target: ExternalLinkTarget._blank,
        nofollow: false,
      },
    } as LinkObject,
  }
}

// ----- Components -----

export function LinkListBlock({
  value,
  onChange,
  configs,
  disabled,
  languageLaneL,
  languageLaneR,
}: BlockProps<LinkListBlockValue>) {
  const {t} = useTranslation()

  const uiList = toListValues(value.linkList)

  return (
    <div>
      <I18nWrapper
        lane1={
          <>
            <div className="wep-label">
              <Form.ControlLabel style={{display: 'inline-block'}}>
                {t('blocks.title.title')}
              </Form.ControlLabel>
            </div>
            <Input
              value={value.title[languageLaneL]}
              onChange={(val) => onChange(val, ['title', languageLaneL])}
            />
          </>
        }
        lane2={
          languageLaneR ? (
            <>
              <div className="wep-label">
                <Form.ControlLabel style={{display: 'inline-block'}}>
                  {t('blocks.title.title')}
                </Form.ControlLabel>
              </div>
              <Input
                disabled={true}
                value={value.title[languageLaneR]}
                onChange={(val) => onChange(val, ['title', languageLaneR])}
              />
            </>
          ) : null
        }
      />

      <div style={{marginTop: 20, marginBottom: 50, width: '50%'}}>
        <SelectPicker
          value={value.listType}
          defaultValue={ListTypeBlockValue.list}
          cleanable={false}
          searchable={false}
          onChange={(val) => onChange(val, ['listType'])}
          data={[
            {label: 'Einfach', value: ListTypeBlockValue.list},
            {label: 'Unsortiert', value: ListTypeBlockValue.unorderedList},
            {label: 'Nummeriert', value: ListTypeBlockValue.orderedList},
          ]}
          style={{width: '50%', paddingTop: 10}}
        />
      </div>

      <ListInput<LinkListBlockValueItem>
        value={uiList}
        disabled={disabled}
        defaultValue={() => makeDefaultItem(configs)}
        onChange={(items) => {
          const nextUI = isFunctionalUpdate(items) ? items(uiList) : items
          const nextStorage = fromListValues(nextUI, value.linkList)
          onChange(nextStorage, ['linkList'])
        }}>
        {(props) => (
          <LinkListElement
            {...props}
            configs={configs}
            languageLaneL={languageLaneL}
            languageLaneR={languageLaneR}
          />
        )}
      </ListInput>
    </div>
  )
}

export function LinkListElement({
  value,
  configs,
  onChange,
  languageLaneL,
  languageLaneR,
}: FieldProps<LinkListBlockValueItem> & {
  configs: Configs
  languageLaneL: string
  languageLaneR: string
}) {
  return (
    <div style={{flexGrow: 1}}>
      <Input
        placeholder="Link Text"
        value={value.linkText[languageLaneL]}
        onChange={(val) =>
          onChange(
            update(value, {
              linkText: {[languageLaneL]: {$set: val}},
            })
          )
        }
      />
      <br />
      <LinkComponent
        value={value.link}
        configs={configs}
        refConfig={{
          page: {scope: 'local'},
          news: {scope: 'local'},
          event: {scope: 'local'},
          funding: {scope: 'local'},
          impulseTopic: {scope: 'local'},
          media: {scope: 'local'},
          multimedia: {scope: 'local'},
        }}
        languageLaneL={languageLaneL}
        languageLaneR={languageLaneR}
        onChange={(val) =>
          onChange(
            update(value, {
              link: {$set: val as LinkObject},
            })
          )
        }
      />
    </div>
  )
}
